import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

import './KPIChartD3.css'; // Import the CSS file for animations

const KPIChart = ({ data, color, colorBg, index, setHoveredData }) => {
  const ref = useRef();
  const [containerWidth, setContainerWidth] = useState(0); // State to store container width

  useEffect(() => {
    // Function to update container width
    const updateWidth = () => {
      if (ref.current) {
        const newWidth = ref.current.parentElement.clientWidth;
        setContainerWidth(newWidth);
      }
    };

    // Create a ResizeObserver to monitor parent container size changes
    const resizeObserver = new ResizeObserver(() => updateWidth());
    if (ref.current && ref.current.parentElement) {
      resizeObserver.observe(ref.current.parentElement);
    }

    // Initial width update
    updateWidth();

    // Cleanup observer on component unmount
    return () => {
      if (ref.current && ref.current.parentElement) {
        resizeObserver.unobserve(ref.current.parentElement);
      }
    };
  }, []);

  useEffect(() => {
    if (containerWidth === 0) return; // Avoid rendering if width is not set

    // Clear previous SVG content to prevent duplicates
    d3.select(ref.current).selectAll('*').remove();

    // Format chart data with date conversion
    const chartData = data.map((trend) => ({
      ...trend,
      date: new Date(trend.date).getTime(),
    }));

    // Set up dimensions
    const width = containerWidth; // Use dynamic container width
    const height = 60; // Reduced height for better proportions
    const margin = { top: 13, right: 6, bottom: 6, left: 4 }; // Adjusted margins for spacing

    // Create scales
    const xScale = d3
      .scaleTime()
      .domain(d3.extent(chartData, (d) => d.date))
      .range([margin.left, width - margin.right]);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(chartData, (d) => d.value)])
      .range([height - margin.bottom, margin.top]);

    // Create SVG container
    const svg = d3
      .select(ref.current)
      .attr('class', 'kpi-chart')
      .attr('viewBox', `0 0 ${width} ${height}`) // Responsive viewBox
      .style('width', '100%') // Responsive width
      .style('height', 'auto') // Maintain aspect ratio
      .style('overflow', 'visible');

    // Line generator
    const line = d3
      .line()
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.value))
      .curve(d3.curveMonotoneX);

    // Draw line with animation class
    svg
      .append('path')
      .datum(chartData)
      .attr('fill', 'none')
      .attr('stroke', color)
      .attr('stroke-width', 2) // Reduced stroke width for better proportions
      .attr('d', line)
      .attr('class', 'line-animate');

    // Draw dots with animation class
    const dots = svg
      .selectAll('.dot')
      .data(chartData)
      .join('circle')
      .attr('class', 'dot dot-animate')
      .attr('cx', (d) => xScale(d.date))
      .attr('cy', (d) => yScale(d.value))
      .attr('r', 3) // Reduced radius for better proportions
      .attr('fill', colorBg)
      .attr('stroke', color)
      .attr('stroke-width', 1.5); // Adjust stroke width for dots

    // Add hover line (initially hidden)
    const hoverLine = svg
      .append('line')
      .attr('class', 'hover-line hover-line-animate')
      .attr('stroke', color)
      .attr('stroke-width', 1)
      .attr('y1', margin.top + 1)
      .attr('opacity', 0.3)
      .style('stroke-dasharray', '2,2')
      .attr('y2', height - (margin.bottom + 1))
      .style('display', 'none');

    // Select the latest data point by default
    const latestPoint = chartData[chartData.length - 1];
    if (latestPoint) {
      setHoveredData((prev) => ({
        ...prev,
        [index]: { value: latestPoint.value, date: new Date(latestPoint.date) },
      }));

      hoverLine
        .style('display', null)
        .attr('x1', xScale(latestPoint.date))
        .attr('x2', xScale(latestPoint.date));

      dots
        .filter((d) => d.date === latestPoint.date)
        .attr('stroke-width', 2)
        .attr('r', 4)
        .attr('fill', getAdjustedColor(colorBg));
    }

    // Add overlay for capturing mouse movements
    svg
      .append('rect')
      .attr('x', margin.left)
      .attr('y', margin.top)
      .attr('width', width - margin.left - margin.right)
      .attr('height', height - margin.top - margin.bottom)
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .on('mousemove', function (event) {
        const [mouseX] = d3.pointer(event, this);
        const date = xScale.invert(mouseX);

        const bisectDate = d3.bisector((d) => d.date).left;
        let i = bisectDate(chartData, date);
        if (i > 0 && i < chartData.length) {
          const prevPoint = chartData[i - 1];
          const nextPoint = chartData[i];
          i = date - prevPoint.date > nextPoint.date - date ? i : i - 1;
        }
        const nearestPoint = chartData[i];
        if (nearestPoint) {
          setHoveredData((prev) => ({
            ...prev,
            [index]: {
              date: new Date(nearestPoint.date),
              value: nearestPoint.value,
              source: nearestPoint.source?.name,
              reportName: nearestPoint.source?.reportName,
            }
          }));

          hoverLine
            .style('display', null)
            .attr('x1', xScale(nearestPoint.date))
            .attr('x2', xScale(nearestPoint.date));

          dots.attr('fill', colorBg)
            .attr('stroke-width', 1.5)
            .attr('r', 3);

          dots
            .filter((d) => d.date === nearestPoint.date)
            .attr('stroke-width', 2)
            .attr('fill', getAdjustedColor(colorBg));
        }
      })
      .on('mouseout', function () {
        const latestPoint = chartData[chartData.length - 1];

        if (latestPoint) {
          setHoveredData((prev) => ({
            ...prev,
            [index]: {
              value: latestPoint.value,
              date: new Date(latestPoint.date),
              source: latestPoint.source?.name,
              reportName: latestPoint.source?.reportName,
            },
          }));

          hoverLine
            .style('display', null)
            .attr('x1', xScale(latestPoint.date))
            .attr('x2', xScale(latestPoint.date));

          dots.attr('fill', colorBg)
            .attr('stroke-width', 1.5)
            .attr('r', 3);

          dots
            .filter((d) => d.date === latestPoint.date)
            .attr('stroke-width', 2)
            .attr('fill', getAdjustedColor(colorBg));
        }
      });
  }, [data, color, colorBg, index, setHoveredData, containerWidth]);

  return <div className="kpi-chart-container"><svg ref={ref} width="100%" height="40"></svg></div>;
};

// Utility function to get CSS variable value
const getCSSVariable = (varName) => {
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
};

// Utility function to adjust color lightness
const adjustColorLightness = (hexColor, percent) => {
  // If no color provided, use primary color from CSS variables
  if (!hexColor) {
    hexColor = getCSSVariable('--color-primary');
  }
  
  // Convert hex to RGB
  let r = parseInt(hexColor.substring(1,3), 16);
  let g = parseInt(hexColor.substring(3,5), 16);
  let b = parseInt(hexColor.substring(5,7), 16);

  // Adjust lightness
  r = parseInt(r * (100 + percent) / 100);
  g = parseInt(g * (100 + percent) / 100);
  b = parseInt(b * (100 + percent) / 100);

  // Ensure values stay within bounds
  r = Math.min(255, Math.max(0, r));
  g = Math.min(255, Math.max(0, g));
  b = Math.min(255, Math.max(0, b));

  // Convert back to hex
  const rr = ((r.toString(16).length === 1) ? "0" + r.toString(16) : r.toString(16));
  const gg = ((g.toString(16).length === 1) ? "0" + g.toString(16) : g.toString(16));
  const bb = ((b.toString(16).length === 1) ? "0" + b.toString(16) : b.toString(16));

  return "#" + rr + gg + bb;
};

// Get adjusted color based on theme
const getAdjustedColor = (hexColor) => {
  const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return isDarkMode ? adjustColorLightness(hexColor, 20) : adjustColorLightness(hexColor, -20);
};

export default KPIChart;
